import * as t from './actionTypes';
import type {
  Member,
  MemberQuery,
  Project,
  Role,
  Team,
  TeamQuery,
  Permission,
  DeliveryTarget,
} from './types';

/*-------------------------------------------------------------------------------*/
/* MEMBERS                                                                       */
/*-------------------------------------------------------------------------------*/

// FETCH_ORGANIZATION_MEMBERS
export const fetchOrganizationMembersRequest = (query: MemberQuery) => ({
  type: t.FETCH_ORGANIZATION_MEMBERS_REQUEST,
  payload: query,
});

export const fetchOrganizationMembersResponse = (members: Member[]) => ({
  type: t.FETCH_ORGANIZATION_MEMBERS_RESPONSE,
  payload: members,
});

export const fetchOrganizationMembersError = (error: string) => ({
  type: t.FETCH_ORGANIZATION_MEMBERS_ERROR,
  payload: error,
  error: true,
});

// CREATE_ORGANIZATION_MEMBER
export const createOrganizationMemberRequest = (member: Member) => ({
  type: t.CREATE_ORGANIZATION_MEMBER_REQUEST,
  payload: member,
});

export const createOrganizationMemberResponse = (member: Member) => ({
  type: t.CREATE_ORGANIZATION_MEMBER_RESPONSE,
  payload: member,
});

export const createOrganizationMemberError = (error: string) => ({
  type: t.CREATE_ORGANIZATION_MEMBER_ERROR,
  payload: error,
  error: true,
});

// UPDATE_ORGANIZATION_MEMBER
export const updateOrganizationMemberRequest = (member: Member, bulkAction = false) => ({
  type: t.UPDATE_ORGANIZATION_MEMBER_REQUEST,
  payload: member,
  bulkAction,
});

export const updateOrganizationMemberResponse = (member: Member) => ({
  type: t.UPDATE_ORGANIZATION_MEMBER_RESPONSE,
  payload: member,
});

export const updateOrganizationMemberError = (error: string) => ({
  type: t.UPDATE_ORGANIZATION_MEMBER_ERROR,
  payload: error,
  error: true,
});

// UPDATE_ORGANIZATION_MEMBERSHIP
export const updateOrganizationMembershipRequest = (member: Member, bulkAction = false) => ({
  type: t.UPDATE_ORGANIZATION_MEMBERSHIP_REQUEST,
  payload: member,
  bulkAction,
});

export const updateOrganizationMembershipResponse = (member: Member) => ({
  type: t.UPDATE_ORGANIZATION_MEMBERSHIP_RESPONSE,
  payload: member,
});

export const updateOrganizationMembershipError = (error: string) => ({
  type: t.UPDATE_ORGANIZATION_MEMBERSHIP_ERROR,
  payload: error,
  error: true,
});

// REMOVE_ORGANIZATION_MEMBER
export const removeOrganizationMemberRequest = (member: Member) => ({
  type: t.REMOVE_ORGANIZATION_MEMBER_REQUEST,
  payload: member,
});

export const removeOrganizationMemberResponse = (member: Member) => ({
  type: t.REMOVE_ORGANIZATION_MEMBER_RESPONSE,
  payload: member,
});

export const removeOrganizationMemberError = (error: string) => ({
  type: t.REMOVE_ORGANIZATION_MEMBER_ERROR,
  payload: error,
  error: true,
});

// SEND_PASSWORD_RESET
export const sendPasswordResetRequest = (member: Member) => ({
  type: t.SEND_PASSWORD_RESET_REQUEST,
  payload: member,
});

export const sendPasswordResetResponse = (member: Member) => ({
  type: t.SEND_PASSWORD_RESET_RESPONSE,
  payload: member,
});

export const sendPasswordResetError = (error: string) => ({
  type: t.SEND_PASSWORD_RESET_ERROR,
  payload: error,
  error: true,
});

// DISABLE_TWO_FACTOR_AUTHENTICATION
export const disableTwoFactorAuthenticationRequest = (member: Member) => ({
  type: t.DISABLE_TWO_FACTOR_AUTHENTICATION_REQUEST,
  payload: member,
});

export const disableTwoFactorAuthenticationResponse = (member: Member) => ({
  type: t.DISABLE_TWO_FACTOR_AUTHENTICATION_RESPONSE,
  payload: member,
});

export const disableTwoFactorAuthenticationError = (error: string) => ({
  type: t.DISABLE_TWO_FACTOR_AUTHENTICATION_ERROR,
  payload: error,
  error: true,
});

/*------------------------------------------------------------------------------------------------*/
/* PROJECTS                                                                                       */
/*------------------------------------------------------------------------------------------------*/

// FETCH_ORGANIZATION_PROJECTS
export const fetchOrganizationProjectsRequest = () => ({
  type: t.FETCH_ORGANIZATION_PROJECTS_REQUEST,
});

export const fetchOrganizationProjectsResponse = (projects: Project[]) => ({
  type: t.FETCH_ORGANIZATION_PROJECTS_RESPONSE,
  payload: projects,
});

export const fetchOrganizationProjectsError = (error: string) => ({
  type: t.FETCH_ORGANIZATION_PROJECTS_ERROR,
  payload: error,
  error: true,
});

/*------------------------------------------------------------------------------------------------*/
/* ROLES                                                                                          */
/*------------------------------------------------------------------------------------------------*/

// FETCH_ORGANIZATION_ROLES
export const fetchOrganizationRolesRequest = () => ({
  type: t.FETCH_ORGANIZATION_ROLES_REQUEST,
});

export const fetchOrganizationRolesResponse = (roles: Role[]) => ({
  type: t.FETCH_ORGANIZATION_ROLES_RESPONSE,
  payload: roles,
});

export const fetchOrganizationRolesError = (error: string) => ({
  type: t.FETCH_ORGANIZATION_ROLES_ERROR,
  payload: error,
  error: true,
});

// CREATE_ORGANIZATION_ROLE
export const createOrganizationRoleRequest = (role: Role) => ({
  type: t.CREATE_ORGANIZATION_ROLE_REQUEST,
  payload: role,
});

export const createOrganizationRoleResponse = (role: Role) => ({
  type: t.CREATE_ORGANIZATION_ROLE_RESPONSE,
  payload: role,
});

export const createOrganizationRoleError = (error: string) => ({
  type: t.CREATE_ORGANIZATION_ROLE_ERROR,
  error: true,
  payload: error,
});

// UPDATE_ORGANIZATION_ROLE
export const updateOrganizationRoleRequest = (role: Role) => ({
  type: t.UPDATE_ORGANIZATION_ROLE_REQUEST,
  payload: role,
});

export const updateOrganizationRoleResponse = (role: Role) => ({
  type: t.UPDATE_ORGANIZATION_ROLE_RESPONSE,
  payload: role,
});

export const updateOrganizationRoleError = (error: string) => ({
  type: t.UPDATE_ORGANIZATION_ROLE_ERROR,
  payload: error,
  error: true,
});

/*-------------------------------------------------------------------------------*/
/* TEAMS                                                                         */
/*-------------------------------------------------------------------------------*/

// FETCH_ORGANIZATION_TEAMS
export const fetchOrganizationTeamsRequest = (query: TeamQuery) => ({
  type: t.FETCH_ORGANIZATION_TEAMS_REQUEST,
  payload: query,
});

export const fetchOrganizationTeamsResponse = (teams: Team[]) => ({
  type: t.FETCH_ORGANIZATION_TEAMS_RESPONSE,
  payload: teams,
});

export const fetchOrganizationTeamsError = (error: string) => ({
  type: t.FETCH_ORGANIZATION_TEAMS_ERROR,
  payload: error,
  error: true,
});

/*-------------------------------------------------------------------------------*/
/* PERMISSIONS                                                                   */
/*-------------------------------------------------------------------------------*/

// FETCH_PERMISSIONS
export const fetchPermissionsRequest = () => ({
  type: t.FETCH_PERMISSIONS_REQUEST,
});

export const fetchPermissionsResponse = (permissions: Permission[]) => ({
  type: t.FETCH_PERMISSIONS_RESPONSE,
  payload: permissions,
});

export const fetchPermissionsError = (error: string) => ({
  type: t.FETCH_PERMISSIONS_ERROR,
  payload: error,
  error: true,
});

/*-------------------------------------------------------------------------------*/
/* DELIVERY TARGETS                                                              */
/*-------------------------------------------------------------------------------*/

// FETCH_DELIVERY_TARGETS
export const fetchDeliveryTargetsRequest = () => ({
  type: t.FETCH_DELIVERY_TARGETS_REQUEST,
});

export const fetchDeliveryTargetsResponse = (deliveryTargets: DeliveryTarget[]) => ({
  type: t.FETCH_DELIVERY_TARGETS_RESPONSE,
  payload: deliveryTargets,
});

export const fetchDeliveryTargetsError = (error: string) => ({
  type: t.FETCH_DELIVERY_TARGETS_ERROR,
  payload: error,
  error: true,
});

// CREATE_DELIVERY_TARGET
export const createDeliveryTargetRequest = (deliveryTarget: DeliveryTarget) => ({
  type: t.CREATE_DELIVERY_TARGET_REQUEST,
  payload: deliveryTarget,
});

export const createDeliveryTargetResponse = (deliveryTarget: DeliveryTarget) => ({
  type: t.CREATE_DELIVERY_TARGET_RESPONSE,
  payload: deliveryTarget,
});

export const createDeliveryTargetError = (error: string) => ({
  type: t.CREATE_DELIVERY_TARGET_ERROR,
  payload: error,
  error: true,
});

// UPDATE_DELIVERY_TARGET
export const updateDeliveryTargetRequest = (deliveryTarget: DeliveryTarget) => ({
  type: t.UPDATE_DELIVERY_TARGET_REQUEST,
  payload: deliveryTarget,
});

export const updateDeliveryTargetResponse = (deliveryTarget: DeliveryTarget) => ({
  type: t.UPDATE_DELIVERY_TARGET_RESPONSE,
  payload: deliveryTarget,
});

export const updateDeliveryTargetError = (error: string) => ({
  type: t.UPDATE_DELIVERY_TARGET_ERROR,
  payload: error,
  error: true,
});
